import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaHandshake, FaPlus, FaTimes, FaSpinner, FaUsers, FaCheck, FaRupeeSign, FaEdit, FaTrash } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGrowthPartnersTab = () => {
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('partners');
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [message, setMessage] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = async () => {
    try {
      const [partnersRes, productsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/growth-partners`, { headers: getHeaders() }),
        axios.get(`${API}/admin/products`, { headers: getHeaders() }),
        axios.get(`${API}/admin/partner-pending-users`, { headers: getHeaders() })
      ]);
      setPartners(partnersRes.data || []);
      setProducts(productsRes.data || []);
      setPendingUsers(pendingRes.data || []);
    } catch (err) { /* silenced */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (message) { const t = setTimeout(() => setMessage(null), 4000); return () => clearTimeout(t); } }, [message]);

  // ==================== ADD PARTNER ====================
  const AddPartnerModal = () => {
    const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
    const [submitting, setSubmitting] = useState(false);
    const [created, setCreated] = useState(null);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        const res = await axios.post(`${API}/admin/growth-partners`, form, { headers: getHeaders() });
        setCreated(res.data);
        fetchData();
      } catch (err) {
        setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create partner' });
      } finally { setSubmitting(false); }
    };

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] max-w-md w-full p-6">
          <div className="flex justify-between mb-4">
            <h3 className="text-white font-bold">{created ? 'Partner Created!' : 'Add Growth Partner'}</h3>
            <button onClick={() => setShowAddPartner(false)} className="text-gray-400 hover:text-white"><FaTimes /></button>
          </div>
          {created ? (
            <div className="space-y-3">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm">{created.message}</div>
              <div className="bg-[#0F172A] rounded-lg p-3 space-y-1 text-sm text-gray-300">
                <p><strong>Name:</strong> {created.partner.name}</p>
                <p><strong>Email:</strong> {created.partner.email}</p>
                <p><strong>Password:</strong> <span className="text-[#FFD700] font-mono">{created.temporary_password}</span></p>
              </div>
              <button onClick={() => setShowAddPartner(false)} className="w-full py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold">Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Full Name" className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="Email" className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              <input value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Password (min 6 chars)" className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              <button type="submit" disabled={submitting} className="w-full py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create Growth Partner'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  // ==================== PRODUCT FORM ====================
  const ProductFormModal = ({ product, onClose }) => {
    const [form, setForm] = useState({
      name: product?.name || '', mrp: product?.mrp || '', commission_percent: product?.commission_percent || '', description: product?.description || ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        if (product) {
          await axios.put(`${API}/admin/products/${product.id}`, form, { headers: getHeaders() });
          setMessage({ type: 'success', text: 'Product updated' });
        } else {
          await axios.post(`${API}/admin/products`, form, { headers: getHeaders() });
          setMessage({ type: 'success', text: 'Product created' });
        }
        fetchData();
        onClose();
      } catch (err) {
        setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed' });
      } finally { setSubmitting(false); }
    };

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] max-w-md w-full p-6">
          <div className="flex justify-between mb-4">
            <h3 className="text-white font-bold">{product ? 'Edit Product' : 'Add Product'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><FaTimes /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-gray-400 text-xs">Product Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="text-gray-400 text-xs">MRP (incl. GST) *</label>
              <input type="number" value={form.mrp} onChange={e => setForm({...form, mrp: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              {form.mrp > 0 && <p className="text-gray-500 text-xs mt-1">Base Price (excl. 18% GST): ₹{Math.round(form.mrp / 1.18)}</p>}
            </div>
            <div>
              <label className="text-gray-400 text-xs">Commission % *</label>
              <input type="number" step="0.1" value={form.commission_percent} onChange={e => setForm({...form, commission_percent: e.target.value})} required className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
              {form.mrp > 0 && form.commission_percent > 0 && (
                <p className="text-[#FFD700] text-xs mt-1">Partner earns: ₹{Math.round((form.mrp / 1.18) * form.commission_percent / 100)} per sale</p>
              )}
            </div>
            <div>
              <label className="text-gray-400 text-xs">Description</label>
              <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <button type="submit" disabled={submitting} className="w-full py-2 bg-[#FFD700] text-[#0A1929] rounded-lg font-bold disabled:opacity-50">
              {submitting ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Delete "${productName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers: getHeaders() });
      setMessage({ type: 'success', text: 'Product deleted' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete product' });
    }
  };

  const handleApprovePartnerUser = async (userId, userName) => {
    if (!window.confirm(`Approve ${userName}? This will count as a sale for the Growth Partner.`)) return;
    try {
      await axios.post(`${API}/admin/clients/${userId}/approve`, {}, { headers: getHeaders() });
      setMessage({ type: 'success', text: `${userName} approved. Sale recorded.` });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to approve' });
    }
  };

  const handleRejectPartnerUser = async (userId, userName) => {
    const reason = window.prompt(`Reason for rejecting ${userName}:`);
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/clients/${userId}/reject`, { reason }, { headers: getHeaders() });
      setMessage({ type: 'success', text: `${userName} rejected.` });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to reject' });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-10"><FaSpinner className="animate-spin text-[#FFD700] text-2xl" /></div>;

  const sections = [
    { id: 'partners', label: 'Partners', count: partners.length },
    { id: 'products', label: 'Products', count: products.length },
    { id: 'pending', label: 'Pending Approvals', count: pendingUsers.length },
  ];

  return (
    <div className="space-y-6" data-testid="admin-growth-partners-tab">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>{message.text}</div>
      )}

      {/* Section Switcher */}
      <div className="flex gap-2">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeSection === s.id ? 'bg-[#FFD700] text-[#0A1929]' : 'bg-[#1A2942] text-gray-400 border border-[#2D5A8F]'}`}
          >
            {s.label} {s.count > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">{s.count}</span>}
          </button>
        ))}
      </div>

      {/* PARTNERS SECTION */}
      {activeSection === 'partners' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-bold text-lg">Growth Partners</h3>
            <button onClick={() => setShowAddPartner(true)} className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg text-sm font-bold" data-testid="add-partner-btn">
              <FaPlus /> Add Partner
            </button>
          </div>
          {partners.length === 0 ? (
            <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-8 text-center text-gray-400">No growth partners yet</div>
          ) : (
            <div className="grid gap-4">
              {partners.map(p => (
                <div key={p.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-bold">{p.name}</h4>
                      <p className="text-gray-400 text-sm">{p.email} | {p.phone || 'N/A'}</p>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-[#FFD700] font-bold text-lg">{p.total_users_added || 0}</p>
                        <p className="text-gray-500 text-xs">Total Added</p>
                      </div>
                      <div className="text-center">
                        <p className="text-green-400 font-bold text-lg">{p.approved_users || 0}</p>
                        <p className="text-gray-500 text-xs">Approved</p>
                      </div>
                      <div className="text-center">
                        <p className="text-amber-400 font-bold text-lg">{p.pending_users || 0}</p>
                        <p className="text-gray-500 text-xs">Pending</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PRODUCTS SECTION */}
      {activeSection === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-bold text-lg">Products & Commissions</h3>
            <button onClick={() => { setEditingProduct(null); setShowAddProduct(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#0A1929] rounded-lg text-sm font-bold" data-testid="add-product-btn">
              <FaPlus /> Add Product
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-[#2D5A8F]/50">
                  <th className="text-left py-3 px-4">Product</th>
                  <th className="text-right py-3 px-4">MRP</th>
                  <th className="text-right py-3 px-4">Base Price</th>
                  <th className="text-right py-3 px-4">Commission %</th>
                  <th className="text-right py-3 px-4">Partner Earns</th>
                  <th className="text-center py-3 px-4">Status</th>
                  <th className="text-center py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const base = Math.round(p.mrp / 1.18);
                  return (
                    <tr key={p.id} className="border-b border-[#2D5A8F]/30 bg-[#0F172A]">
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{p.name}</p>
                        <p className="text-gray-500 text-xs">{p.description}</p>
                      </td>
                      <td className="py-3 px-4 text-right text-white">₹{p.mrp}</td>
                      <td className="py-3 px-4 text-right text-gray-400">₹{base}</td>
                      <td className="py-3 px-4 text-right text-[#FFD700] font-bold">{p.commission_percent}%</td>
                      <td className="py-3 px-4 text-right text-green-400 font-medium">₹{Math.round(base * p.commission_percent / 100)}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${p.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => { setEditingProduct(p); setShowAddProduct(true); }} className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30" data-testid={`edit-product-${p.id}`}><FaEdit size={12} /></button>
                          <button onClick={() => handleDeleteProduct(p.id, p.name)} className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" data-testid={`delete-product-${p.id}`}><FaTrash size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PENDING APPROVALS SECTION */}
      {activeSection === 'pending' && (
        <div className="space-y-4">
          <h3 className="text-white font-bold text-lg">Pending Partner User Approvals</h3>
          {pendingUsers.length === 0 ? (
            <div className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-8 text-center">
              <FaCheck className="text-green-400 text-3xl mx-auto mb-3" />
              <p className="text-gray-400">No pending approvals</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingUsers.map(u => (
                <div key={u.id} className="bg-[#0F172A] rounded-xl border border-[#2D5A8F]/50 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 flex-1 text-sm">
                      <div><p className="text-white font-medium">{u.name}</p><p className="text-gray-500 text-xs">Name</p></div>
                      <div><p className="text-gray-300">{u.email}</p><p className="text-gray-500 text-xs">Email</p></div>
                      <div><p className="text-gray-300">{u.phone || '-'}</p><p className="text-gray-500 text-xs">Phone</p></div>
                      <div><p className="text-blue-400">{u.role}</p><p className="text-gray-500 text-xs">Role</p></div>
                      <div><p className="text-[#FFD700]">{u.partner_name}</p><p className="text-gray-500 text-xs">Partner</p></div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleApprovePartnerUser(u.id, u.name)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium" data-testid={`approve-partner-user-${u.id}`}>
                        <FaCheck className="inline mr-1" /> Approve
                      </button>
                      <button onClick={() => handleRejectPartnerUser(u.id, u.name)} className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm border border-red-500/30">
                        <FaTimes className="inline mr-1" /> Reject
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Product: {u.product_name} | Added: {u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '-'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAddPartner && <AddPartnerModal />}
      {showAddProduct && <ProductFormModal product={editingProduct} onClose={() => { setShowAddProduct(false); setEditingProduct(null); }} />}
    </div>
  );
};

export default AdminGrowthPartnersTab;
